import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useMockMode } from "@/contexts/MockModeContext";
import { router } from "../router";
import { RoutePathEnum } from "@/routes/paths";
import { sessionChannel } from "@/services/session-channel";
import { useUserStore } from "@/stores/use-user-store";

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { loadUserData, setIsLoading } = useUserStore((state) => ({
    loadUserData: state.loadUserData,
    setIsLoading: state.setIsLoading,
  }));
  const { isMockMode, isMswReady } = useMockMode();
  const queryClient = useQueryClient();
  // Previne execução dupla em Strict Mode (remonta o useEffect)
  const hasCheckedAuth = useRef(false);
  const recoveryAbortController = useRef<AbortController | null>(null);

  useEffect(
    function subscribeToLogout() {
      return sessionChannel.subscribeToLogout(() => {
        recoveryAbortController.current?.abort();
        useUserStore.getState().reset();
        queryClient.clear();
        router.navigate(RoutePathEnum.LOGIN);
      });
    },
    [queryClient],
  );

  useEffect(
    function initializeAuthentication() {
      const checkAuthentication = async () => {
        if (hasCheckedAuth.current) {
          return;
        }

        // Em modo mock, aguardar MSW estar pronto
        if (isMockMode && !isMswReady) {
          console.log("AuthProvider: Aguardando MSW...");
          return;
        }

        hasCheckedAuth.current = true;
        setIsLoading(true);
        const abortController = new AbortController();
        recoveryAbortController.current = abortController;

        try {
          await loadUserData(undefined, abortController.signal);
        } finally {
          setIsLoading(false);
        }
      };

      checkAuthentication();
    },
    [isMockMode, isMswReady, loadUserData, setIsLoading],
  );

  return <>{children}</>;
}
